import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import './App.css';

// Import context
import { AuthProvider } from './context/AuthContext';
import { TreatmentProvider } from './context/TreatmentContext';

// Import components
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';

// Import pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/Home';
import About from './pages/About';
import ChapterViewer from './pages/ChapterViewer';
import ArticleReader from './pages/ArticleReader';
// eslint-disable-next-line no-unused-vars
import ResearchReader from './pages/ResearchReader';
import AcharyaSushruta from './pages/AcharyaSushruta';
import ShalyaTantra from './pages/ShalyaTantra';
import SushrutaSamhita from './pages/SushrutaSamhita';
import Textbooks from './pages/Textbooks';
import Articles from './pages/Articles';
import Research from './pages/Research';
import Jurisprudence from './pages/Jurisprudence';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Biography from './pages/Biography';
import Blog from './pages/Blog';
import SyllabusTracker from './pages/SyllabusTracker';
import Simulations from './pages/Simulations';
import News from './pages/News';
import Forum from './pages/Forum';
import Team from './pages/Team';
import Contact from './pages/Contact';
import TreatmentGateway from './pages/treatment/TreatmentGateway';
import TreatmentDashboard from './pages/treatment/TreatmentDashboard';
import TreatmentPatients from './pages/treatment/TreatmentPatients';
import PrakritiAssess from './pages/treatment/PrakritiAssess';
import Recommendations from './pages/treatment/Recommendations';
import TreatmentPlansList from './pages/treatment/TreatmentPlansList';
import TreatmentPlanDetail from './pages/treatment/TreatmentPlanDetail';
import Catalog from './pages/treatment/Catalog';
import BotWelcomePopup from './components/BotWelcomePopup';
import Profile from './pages/Profile';

function App() {
  return (
    <AuthProvider>
      <TreatmentProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public routes - Login and Signup without Navbar/Footer */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Public Home page with Navbar/Footer */}
              <Route
                path="/"
                element={
                  <>
                    <Header />
                    <main>
                      <Home />
                    </main>
                    <Footer />
                  </>
                }
              />

              {/* Public Pitch / About Page */}
              <Route
                path="/about"
                element={
                  <>
                    <Header />
                    <main>
                      <About />
                    </main>
                    <Footer />
                  </>
                }
              />

              {/* Public SEO Chapter Reading Routes */}
              <Route
                path="/textbooks/:slug"
                element={
                  <>
                    <Header />
                    <main>
                      <ChapterViewer />
                    </main>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/chapters/:slug"
                element={
                  <>
                    <Header />
                    <main>
                      <ChapterViewer />
                    </main>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/books/:slug"
                element={
                  <>
                    <Header />
                    <main>
                      <ChapterViewer />
                    </main>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/articles/:slug"
                element={
                  <>
                    <Header />
                    <main>
                      <ArticleReader />
                    </main>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/research/:slug"
                element={
                  <>
                    <Header />
                    <main>
                      <ResearchReader />
                    </main>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/thesis/:slug"
                element={
                  <>
                    <Header />
                    <main>
                      <ResearchReader />
                    </main>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/textbooks/:bookId/chapter/:chapterNum"
                element={
                  <>
                    <Header />
                    <main>
                      <ChapterViewer />
                    </main>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/sushruta-samhita/:slug"
                element={
                  <>
                    <Header />
                    <main>
                      <ChapterViewer />
                    </main>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/sushruta-samhita/:bookId/chapter/:chapterNum"
                element={
                  <>
                    <Header />
                    <main>
                      <ChapterViewer />
                    </main>
                    <Footer />
                  </>
                }
              />

              {/* Public pages - no login required (read actions show login prompt for guests) */}
              <Route path="/team" element={<><Header /><main><Team /></main><Footer /></>} />
              <Route path="/contact" element={<><Header /><main><Contact /></main><Footer /></>} />
              <Route path="/sushruta-samhita" element={<><Header /><main><SushrutaSamhita /></main><Footer /></>} />
              <Route path="/textbooks" element={<><Header /><main><Textbooks /></main><Footer /></>} />
              <Route path="/articles" element={<><Header /><main><Articles /></main><Footer /></>} />
              <Route path="/blog" element={<><Header /><main><Blog /></main><Footer /></>} />
              <Route path="/research" element={<><Header /><main><Research /></main><Footer /></>} />
              <Route path="/jurisprudence" element={<><Header /><main><Jurisprudence /></main><Footer /></>} />
              <Route path="/treatment" element={<><Header /><main><TreatmentGateway /></main><Footer /></>} />
              <Route path="/acharya-sushruta" element={<><Header /><main><AcharyaSushruta /></main><Footer /></>} />
              <Route path="/shalya-tantra" element={<><Header /><main><ShalyaTantra /></main><Footer /></>} />
              <Route path="/privacy-policy" element={<><Header /><main><PrivacyPolicy /></main><Footer /></>} />
              <Route path="/privacy" element={<><Header /><main><PrivacyPolicy /></main><Footer /></>} />
              <Route path="/terms" element={<><Header /><main><TermsOfService /></main><Footer /></>} />

              {/* Protected routes - All other pages require authentication */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Header />
                    <main>
                      <Routes>
                        <Route path="/workspace" element={<Navigate to="/" replace />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/biography" element={<Biography />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/syllabus-tracker" element={<SyllabusTracker />} />
                        <Route path="/simulations" element={<Simulations />} />
                        <Route path="/news" element={<News />} />
                        <Route path="/forum" element={<Forum />} />
                        {/* Treatment / AYUSH Care - nested routes */}
                        <Route path="/treatment" element={<Outlet />}>
                          <Route index element={<ProtectedRoute allowedRoles={['Doctor', 'Admin']}><TreatmentDashboard /></ProtectedRoute>} />
                          <Route path="patients" element={<ProtectedRoute allowedRoles={['Doctor', 'Admin']}><TreatmentPatients /></ProtectedRoute>} />
                          <Route path="assess" element={<ProtectedRoute allowedRoles={['Doctor', 'Admin']}><PrakritiAssess /></ProtectedRoute>} />
                          <Route path="recommendations" element={<ProtectedRoute allowedRoles={['Doctor', 'Admin']}><Recommendations /></ProtectedRoute>} />
                          <Route path="plans" element={<ProtectedRoute allowedRoles={['Doctor', 'Admin']}><TreatmentPlansList /></ProtectedRoute>} />
                          <Route path="plans/:id" element={<ProtectedRoute allowedRoles={['Doctor', 'Admin']}><TreatmentPlanDetail /></ProtectedRoute>} />
                          <Route path="catalog" element={<Catalog />} />
                        </Route>
                        {/* Redirect any unknown routes to home */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </main>
                    <Footer />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <BotWelcomePopup />
          </div>
        </Router>
      </TreatmentProvider>
    </AuthProvider>
  );
}

export default App; 